// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="Timeline" set="mytaible-shadcn-studio" verified=tbd
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TimelineItem {
  label: string;
  description?: string;
  icon?: React.ReactNode;
  date?: string;
}

export interface TimelineProps extends React.HTMLAttributes<HTMLOListElement> {
  variant?: 'basic' | 'with-icon' | 'centered';
  items: TimelineItem[];
}

// ─── Timeline ─────────────────────────────────────────────────────────────────

const Timeline = React.forwardRef<HTMLOListElement, TimelineProps>(
  ({ className, variant = 'basic', items, ...props }, ref) => {
    if (variant === 'centered') {
      return (
        <ol ref={ref} className={cn('relative', className)} {...props}>
          {items.map((item, index) => (
            <li key={index} className="mb-8 flex gap-x-4 last:mb-0">
              {/* Left side — alternating */}
              <div className={cn('flex-1 pb-8', index % 2 === 0 ? 'text-right' : '')}>
                {index % 2 === 0 && (
                  <>
                    <p className="text-sm font-medium text-foreground">{item.label}</p>
                    {item.description && (
                      <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
                    )}
                    {item.date && (
                      <span className="text-xs text-muted-foreground">{item.date}</span>
                    )}
                  </>
                )}
              </div>
              {/* Center dot + line */}
              <div className="flex flex-col items-center">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  {item.icon ?? <span className="h-2 w-2 rounded-full bg-primary-foreground" />}
                </div>
                {index < items.length - 1 && <div className="mt-1 h-full w-px bg-border" />}
              </div>
              {/* Right side — alternating */}
              <div className={cn('flex-1 pb-8', index % 2 !== 0 ? 'text-left' : '')}>
                {index % 2 !== 0 && (
                  <>
                    <p className="text-sm font-medium text-foreground">{item.label}</p>
                    {item.description && (
                      <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
                    )}
                    {item.date && (
                      <span className="text-xs text-muted-foreground">{item.date}</span>
                    )}
                  </>
                )}
              </div>
            </li>
          ))}
        </ol>
      );
    }

    return (
      <ol ref={ref} className={cn('relative', className)} {...props}>
        {items.map((item, index) => (
          <li key={index} className="relative pb-8 pl-8 last:pb-0">
            {/* Connector line */}
            {index < items.length - 1 && (
              <div className="absolute left-3 top-3 h-full w-px bg-border" aria-hidden="true" />
            )}
            {/* Dot / icon */}
            <div
              className={cn(
                'absolute left-0 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground',
                variant === 'with-icon' && item.icon && 'text-xs',
              )}
              aria-hidden="true"
            >
              {variant === 'with-icon' && item.icon ? (
                item.icon
              ) : (
                <span className="h-2 w-2 rounded-full bg-primary-foreground" />
              )}
            </div>
            {/* Content */}
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-foreground">{item.label}</p>
                {item.date && <span className="text-xs text-muted-foreground">{item.date}</span>}
              </div>
              {item.description && (
                <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
              )}
            </div>
          </li>
        ))}
      </ol>
    );
  },
);
Timeline.displayName = 'Timeline';

export { Timeline };
